// IndexedDB storage for the offline-first data layer.
//
// One database, six stores:
//   habits      — keyPath 'id' (server uuid or 'tmp_*' for offline-created)
//   completions — keyPath 'key' = `${habit_id}-${date}`, indexed by 'date'
//   sickWeeks   — keyPath 'key' = `${habit_id}-${week_start}`, indexed by 'week_start'
//   user        — keyPath 'id' (cached profile from /api/me)
//   outbox      — keyPath 'id' (uuid), indexed by 'createdAt'
//   meta        — keyPath 'key' (free-form: lastSyncAt, schemaVersion, ...)
//
// The composite-key approach for completions/sickWeeks mirrors the in-memory
// keying already used by useCompletions.js / useSickWeeks.js, so the hooks
// can swap their Map for an IDB read with minimal shape change.

import { openDB } from 'idb';

const DB_NAME = 'steadily-offline';
const DB_VERSION = 1;

let dbPromise = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('habits')) {
          db.createObjectStore('habits', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('completions')) {
          const s = db.createObjectStore('completions', { keyPath: 'key' });
          s.createIndex('date', 'date');
          s.createIndex('habit_id', 'habit_id');
        }
        if (!db.objectStoreNames.contains('sickWeeks')) {
          const s = db.createObjectStore('sickWeeks', { keyPath: 'key' });
          s.createIndex('week_start', 'week_start');
          s.createIndex('habit_id', 'habit_id');
        }
        if (!db.objectStoreNames.contains('user')) {
          db.createObjectStore('user', { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains('outbox')) {
          const s = db.createObjectStore('outbox', { keyPath: 'id' });
          s.createIndex('createdAt', 'createdAt');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta', { keyPath: 'key' });
        }
      },
    });
  }
  return dbPromise;
}

export function completionKey(habitId, date) {
  return `${habitId}-${date}`;
}

export function sickWeekKey(habitId, weekStart) {
  return `${habitId}-${weekStart}`;
}

export async function getAll(store) {
  const db = await getDb();
  return db.getAll(store);
}

export async function get(store, key) {
  const db = await getDb();
  return db.get(store, key);
}

export async function put(store, value) {
  const db = await getDb();
  return db.put(store, value);
}

export async function bulkPut(store, values) {
  if (!values || values.length === 0) return;
  const db = await getDb();
  const tx = db.transaction(store, 'readwrite');
  await Promise.all(values.map((v) => tx.store.put(v)));
  await tx.done;
}

export async function del(store, key) {
  const db = await getDb();
  return db.delete(store, key);
}

export async function clear(store) {
  const db = await getDb();
  return db.clear(store);
}

// Replace the full contents of a store with the given values, in one transaction.
// Used after a successful background reconcile so stale rows get dropped.
export async function replaceAll(store, values) {
  const db = await getDb();
  const tx = db.transaction(store, 'readwrite');
  await tx.store.clear();
  if (values && values.length > 0) {
    await Promise.all(values.map((v) => tx.store.put(v)));
  }
  await tx.done;
}

// Range read on an index — used by completions/sickWeeks for windowed loads.
export async function getRangeByIndex(store, indexName, lower, upper) {
  const db = await getDb();
  const range = IDBKeyRange.bound(lower, upper);
  return db.getAllFromIndex(store, indexName, range);
}

export async function getMeta(key) {
  const row = await get('meta', key);
  return row ? row.value : undefined;
}

export async function setMeta(key, value) {
  return put('meta', { key, value });
}

// Test/dev helper — wipes everything. Not used in normal flows.
export async function resetDatabase() {
  const db = await getDb();
  await Promise.all(
    [...db.objectStoreNames].map((name) => db.clear(name))
  );
}
